// The compile the app calls (`CompileService`, SuiteCompile/CompileService.swift): presets from the project, Select and
// Map from the live documents or a checkpoint, then the writer for the preset's format. Every compile records a
// checkpoint on the Mac; here the app records it, through `recordCompileCheckpoint` (the core's typed writes), which
// `compile` calls once Select/Map has succeeded, so a compile that can't be made leaves none, and the Word file's
// custom properties name it. If the documents moved on between the reading and the checkpoint (the app records it in
// another thread), the manuscript is made again from the checkpoint, so the checkpoint is exactly what was compiled.
// A checkpoint whose history hasn't all reached this device is refused, as the Swift's `CompileSource.at` refuses it.
// PDF, FDX and Fountain are W5b's: pass their renderers in.
import type * as A from '@automerge/automerge';
import { editProject, isStageFormat, readFragment, readProject, type CheckpointRecord } from '@writing-suite/core';
import { readByline } from '@writing-suite/core/members';
import { CHECKPOINT_PROPERTY, PROJECT_PROPERTY, READER_PROPERTY, writeDocx } from './docx.ts';
import { writeEpub } from './epub.ts';
import { writeHTML } from './html.ts';
import { isDocument, isPost, publishingAuthor } from './manuscript.ts';
import { buildNarration, narrationText } from './narration.ts';
import { run } from './pipeline.ts';
import { preflight, type PreflightFinding, type PreflightOptions } from './preflight.ts';
import { CompileError, PresetLibrary, projectPresetSources } from './presets.ts';
import { liveSource, penNameOf, sourceAt, sourceMatches, type CompileSource } from './source.ts';
import { printedStageScriptAsync } from './screenplay/stage.ts';
import { writeStageDocx } from './screenplay/stageDocx.ts';
import { writeMulticamDocx } from './screenplay/multicam.ts';
import { writeRadioDocx } from './screenplay/radio.ts';
import { stageUKSetup, stageUKSource } from './screenplay/stageTypst.ts';
import { writeMarkdown, writePlainText } from './text.ts';
import { fileExtension, type AuthorInfo, type CompiledManuscript, type CompilePreset, type OutputFormat } from './types.ts';
import { utf8 } from './zip.ts';

export { CHECKPOINT_PROPERTY, PROJECT_PROPERTY, READER_PROPERTY };

export const MIME_TYPES: Record<OutputFormat, string> = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  epub: 'application/epub+zip',
  markdown: 'text/markdown; charset=utf-8',
  text: 'text/plain; charset=utf-8',
  audiobook: 'audio/mp4',
  html: 'text/html; charset=utf-8',
  fdx: 'application/xml',
  fountain: 'text/plain; charset=utf-8',
};

/** Writers another package supplies (W5b: Typst PDF, FDX, Fountain). */
export interface Renderers {
  pdf?: (m: CompiledManuscript) => Uint8Array | Promise<Uint8Array>;
  fdx?: (m: CompiledManuscript) => Uint8Array | Promise<Uint8Array>;
  fountain?: (m: CompiledManuscript) => string | Promise<string>;
  /** Pages a Typst source sets on: where a UK play's sample ends (`StageUKTypst`); without it they're estimated. */
  typstPages?: (source: string) => Promise<number | null>;
}

export interface RenderOptions {
  /** Dates in the files (DOCX core properties, EPUB `dcterms:modified`); defaults to now. */
  date?: Date;
  /** Word custom document properties. */
  properties?: Record<string, string>;
  renderers?: Renderers;
}

/** `CompileService.render`: the file for a compiled manuscript. */
export async function render(m: CompiledManuscript, o: RenderOptions = {}): Promise<Uint8Array> {
  const format = m.preset.format;
  const r = o.renderers ?? {};
  const unavailable = (what: string) => new CompileError({ type: 'engineUnavailable', what });
  if (m.preset.render.screenplay && format === 'pdf') {
    if (!r.pdf) throw unavailable('PDF output (Typst)');
    return await r.pdf(m);
  }
  switch (format) {
    case 'fdx': if (!r.fdx) throw unavailable('Final Draft output'); return await r.fdx(m);
    case 'fountain': if (!r.fountain) throw unavailable('Fountain output'); return utf8(await r.fountain(m));
    case 'docx':
      // A play as Word: its own styles and sections (a UK sample cut where Typst's pages end).
      if (m.preset.render.screenplay && isStageFormat(m.preset.render.screenplay.format ?? 'screen')) {
        const typstPages = r.typstPages;
        const script = m.preset.render.screenplay.format === 'stage-uk' && m.preset.render.screenplay.sample && typstPages
          ? await printedStageScriptAsync(m, (s) => typstPages(stageUKSource(s, { ...stageUKSetup(m), titlePage: false })))
          : undefined;
        return writeStageDocx(m, { date: o.date, properties: o.properties, script });
      }
      if (m.preset.render.screenplay?.format === 'multicam') return writeMulticamDocx(m, { date: o.date, properties: o.properties });
      if (m.preset.render.screenplay?.format === 'radio') return writeRadioDocx(m, { date: o.date, properties: o.properties });
      return writeDocx(m, { date: o.date, properties: o.properties, year: (o.date ?? new Date()).getFullYear() });
    case 'epub': return writeEpub(m, { date: o.date });
    case 'markdown': return utf8(writeMarkdown(m, { year: (o.date ?? new Date()).getFullYear() }));
    case 'html': return utf8(writeHTML(m));
    case 'text': return utf8(m.preset.render.audio ? narrationText(buildNarration(m)) : writePlainText(m, { year: (o.date ?? new Date()).getFullYear() }));
    case 'audiobook': throw unavailable('Audiobook output');
    case 'pdf': if (!r.pdf) throw unavailable('PDF output (Typst)'); return await r.pdf(m);
  }
}

/** The file's name, as `CompileService.compile` names it. */
export function compiledFilename(m: CompiledManuscript): string {
  const base = m.title.replaceAll('/', '-');
  const document = isDocument(m) ? ` - ${m.sections[0]?.title ?? 'Document'}`
    : isPost(m) ? ` - ${(m.sections.find((s) => s.fragmentID !== null)?.title ?? 'Post').replaceAll('/', '-')}` : '';
  return `${base}${m.preset.blind ? ' (blind)' : ''}${document}.${fileExtension(m.preset.format)}`;
}

/** "<preset> — <date>", the name a compile's checkpoint takes (`date.formatted(date: .abbreviated, time: .shortened)`). */
export function checkpointName(preset: CompilePreset, date: Date, locale?: string): string {
  const day = date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  return `${preset.name} — ${day} at ${time}`;
}

/** The project's presets: built-ins with the project's own (`settings.compilePresets`) over them. */
export function presetLibrary(docs: Map<string, A.Doc<unknown>>): PresetLibrary {
  const project = docs.get('project');
  return new PresetLibrary(project ? projectPresetSources(readProject(project).settings) : {});
}

/** Records a compile's checkpoint through the core's typed writes (`createCheckpoint(… trigger: .compile)`). */
export function recordCompileCheckpoint(docs: Map<string, A.Doc<unknown>>, name: string,
  context: { device: string; author: string; now?: () => Date }): { id: string; changed: string[] } {
  return editProject(docs, context).createCheckpoint(name, 'compile');
}

export interface CompileOptions {
  presetId: string;
  /** A checkpoint's id: compile the project exactly as it recorded it (no new checkpoint is made). */
  at?: string | null;
  /** The post (node or fragment id) a one-post preset compiles. */
  only?: string | null;
  /** Only these fragments (Publish ▸ only the filtered scenes), with the folders above them as headings. */
  limit?: Iterable<string> | null;
  /** Who it's by (`AuthorInfo.publishing` with the project's pen name: see `authorFor`). */
  author: AuthorInfo | null;
  /** The compile's date: `{year}`, file dates, the checkpoint's name. Defaults to now. */
  now?: Date;
  /** Replaces the title-derived short title in running heads. */
  shortTitle?: string | null;
  /** Records the compile's checkpoint once Select/Map has succeeded (usually `recordCompileCheckpoint`) and returns
   *  its id, once `docs` holds the project document that records it. Leave it out for a compile that records none
   *  (previews). It may be asynchronous: an app compiling in a worker records it where the documents are edited. */
  checkpoint?: ((name: string) => string | Promise<string>) | null;
  /** Replaces "<preset> — <date>". */
  checkpointName?: string;
  /** More Word custom properties (a reader's copy adds `WritingSuiteReader`). */
  properties?: Record<string, string>;
  /** A beta reader's mark on every page. */
  readerMark?: string | null;
  /** Saved-search members as the app knows them (else evaluated from the documents). */
  searchMembers?: Record<string, Iterable<string>>;
  renderers?: Renderers;
  /** Run preflight on the output (default true), with these options. */
  preflight?: boolean | PreflightOptions;
  /** For the checkpoint's name. */
  locale?: string;
}

export interface CompileFile { name: string; bytes: Uint8Array; mime: string }

export interface CompileReport {
  presetId: string;
  presetName: string;
  format: OutputFormat;
  filename: string;
  /** The checkpoint the output names (made by this compile, or the one compiled `at`). */
  checkpoint: { id: string; name: string } | null;
  findings: PreflightFinding[];
}

export interface CompileResult { files: CompileFile[]; manuscript: CompiledManuscript; report: CompileReport }

/** Select and Map only (`CompileService.prepare` without the checkpoint): the manuscript and the source it came from. */
export function prepare(docs: Map<string, A.Doc<unknown>>, o: Pick<CompileOptions, 'presetId' | 'at' | 'only' | 'limit' | 'author' | 'now' | 'shortTitle' | 'searchMembers'>):
  { manuscript: CompiledManuscript; source: CompileSource; checkpoint: CheckpointRecord | null } {
  const now = o.now ?? new Date();
  const preset = presetLibrary(docs).preset(o.presetId);
  let checkpoint: CheckpointRecord | null = null;
  let source: CompileSource;
  if (o.at) {
    const project = docs.get('project');
    checkpoint = project ? readProject(project).checkpoints.find((c) => c.id === o.at) ?? null : null;
    if (!checkpoint) throw new Error(`No checkpoint “${o.at}” in this project`);
    source = sourceAt(docs, checkpoint, { now, searchMembers: o.searchMembers });
    if (source.missingFragments.length) {
      const titleOf = (id: string) => {
        const doc = docs.get('fragment.' + id);
        try { return doc ? readFragment(doc).title : null; } catch { return null; }
      };
      throw new CompileError({ type: 'missingFragments', checkpoint: checkpoint.name, fragments: source.missingFragments.map(titleOf) });
    }
  } else {
    source = liveSource(docs, { now, searchMembers: o.searchMembers });
  }
  if (o.shortTitle !== undefined) source.shortTitle = o.shortTitle;
  if (o.limit) source.limit = new Set(o.limit);
  const manuscript = run(preset, source, o.author, { date: now, only: o.only ?? null });
  return { manuscript, source, checkpoint };
}

/** Compiles a project: `compile(docs, { presetId, at?, only?, author, now })` → the files, the manuscript, a report. */
export async function compile(docs: Map<string, A.Doc<unknown>>, o: CompileOptions): Promise<CompileResult> {
  const now = o.now ?? new Date();
  let { manuscript, source, checkpoint: at } = prepare(docs, { ...o, now });
  const preset = manuscript.preset;
  let checkpoint: { id: string; name: string } | null = at ? { id: at.id, name: at.name } : null;
  if (!at && o.checkpoint) {
    const name = o.checkpointName ?? checkpointName(preset, now, o.locale);
    checkpoint = { id: await o.checkpoint(name), name };
    // Typed (or synced) between the reading and the checkpoint: made again from exactly what the checkpoint holds.
    const project = docs.get('project');
    const record = project ? readProject(project).checkpoints.find((c) => c.id === checkpoint!.id) : undefined;
    if (record && !sourceMatches(source, record)) ({ manuscript, source } = prepare(docs, { ...o, now, at: record.id }));
  }
  if (o.readerMark !== undefined) manuscript.readerMark = o.readerMark;
  const properties: Record<string, string> = { [PROJECT_PROPERTY]: source.projectID, ...(o.properties ?? {}) };
  if (checkpoint) properties[CHECKPOINT_PROPERTY] = checkpoint.id;
  const bytes = await render(manuscript, { date: now, properties, renderers: o.renderers });
  const filename = compiledFilename(manuscript);
  const findings = o.preflight === false ? []
    : preflight({ data: bytes, format: preset.format, manuscript }, typeof o.preflight === 'object' ? o.preflight : {});
  return {
    files: [{ name: filename, bytes, mime: MIME_TYPES[preset.format] }],
    manuscript,
    report: { presetId: preset.id, presetName: preset.name, format: preset.format, filename, checkpoint, findings },
  };
}

/** The author a project compiles under: the writer's own details, published under the project's pen name if it has one
 *  (with the pen name's own contact details, when it has them). */
export function authorFor(docs: Map<string, A.Doc<unknown>>, writer: { name: string; surname?: string; contact?: string[] }): AuthorInfo | null {
  const project = docs.get('project');
  const settings = project ? readProject(project).settings : {};
  const author = publishingAuthor({ ...writer, penName: penNameOf(settings) });
  // A co-written book (W9c): the byline's co-authors after the project's own name.
  const byline = readByline(settings);
  if (!author || (!byline.names.length && !byline.roles)) return author;
  // `AuthorInfo.with(_:)`: the names (when there are any) and a musical's roles.
  return { ...author, ...(byline.names.length ? { coAuthors: byline.names, joiner: byline.joiner } : {}), ...(byline.roles ? { roles: byline.roles } : {}) };
}
